import { GetStaticPaths, GetStaticProps } from 'next';

import { Repository } from '@app/config';
import { getDependency } from '@app/utils';

import { CategoryTradeType } from '@domain/entities';
import {
  HighlightRepository,
  PrismicRepository,
  RatingRepository,
  TreeRepository,
} from '@domain/repositories';

import { Category as CategoryList } from '@hoa/config';

import { Category, CategoryProps } from '@hoa/pages/Category';
import { ServiceRequest, ServiceRequestProps } from '@hoa/pages/ServiceRequest';

import { QuoteUrlSchema, TradeUrlSchema } from '@hoa/schema';

import { getPrismicLang, getTrade } from '@hoa/utils';

import { LocaleType, PageParamsType } from '@hoa/types';
import { removeSubCategoryFromTree } from '@hoa/utils/removeSubCategoryFromTree';

export const getStaticPaths: GetStaticPaths<{
  lang: string;
  trade: string;
  quote: string;
}> = async () => {
  const pathsForQuote = Object.entries(TradeUrlSchema).flatMap(
    ([lang, value]) => {
      const quote = QuoteUrlSchema[lang as LocaleType];
      return Object.keys(value)
        .filter(key => key !== `terms` && key !== `privacy`)
        .map(key => ({
          params: {
            lang,
            trade: value[key as CategoryTradeType],
            quote,
          },
        }));
    },
  );
  return {
    paths: [...pathsForQuote],
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps<
  ServiceRequestProps,
  PageParamsType & { quote: CategoryTradeType; trade: string }
> = async context => {
  const prismicRepository = getDependency<PrismicRepository>(
    Repository.PRISMIC,
  );
  const highlightRepository = getDependency<HighlightRepository>(
    Repository.HIGHLIGHT,
  );
  const treeRepository = getDependency<TreeRepository>(Repository.TREE);
  const ratingRepository = getDependency<RatingRepository>(Repository.RATING);

  const originalTree = await treeRepository.findOne();

  const tree = removeSubCategoryFromTree(originalTree);

  const lang = context.params?.lang;
  const quote = context.params?.quote;

  const originalTrade = quote && getTrade(lang as LocaleType, quote);
  const categoryId =
    originalTrade && CategoryList[originalTrade as CategoryTradeType];

  const categoryCityPagesRes =
    originalTrade &&
    lang &&
    (await prismicRepository.findCategoryCityPage(
      originalTrade,
      getPrismicLang(lang),
    ));
  const categoryCityPage = categoryCityPagesRes?.results[0];

  const rating = await ratingRepository.findOne({
    category_id: categoryId || 2,
    locale: lang as LocaleType,
  });

  const highlight = await highlightRepository.findOne();

  const isQuote = !(quote && !!originalTrade);

  return {
    props: {
      lang,
      tree,
      isQuote,
      categoryCityPage: categoryCityPage || null,
      rating,
      highlight,
    },
  };
};

const ServiceRequestPage = (
  props: CategoryProps & ServiceRequestProps,
): JSX.Element => {
  if (props.isQuote) return <ServiceRequest {...props} />;

  return (
    <Category
      lang={props.lang}
      rating={props.rating}
      tree={props.tree}
      highlight={props.highlight}
      categoryCityPage={props.categoryCityPage}
    />
  );
};

export default ServiceRequestPage;
